import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import de from './locales/de.json';
import en from './locales/en.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import zhCN from './locales/zh-CN.json';

export const LOCALE_STORAGE_KEY = 'we0_locale';
export const LOCALE_COOKIE_NAME = 'i18n_locale';
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
export const SUPPORTED_LOCALES = ['zh-CN', 'en', 'ja', 'ko', 'fr', 'de', 'it'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'zh-CN';

export const localeLabelMap: Record<Locale, string> = {
  'zh-CN': '中文',
  en: 'English',
  ja: '日本語',
  ko: '한국어',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
};

type LocaleMessages = Record<string, unknown>;

const messagesByLocale: Record<Locale, LocaleMessages> = {
  'zh-CN': zhCN as LocaleMessages,
  en: en as LocaleMessages,
  ja: ja as LocaleMessages,
  ko: ko as LocaleMessages,
  fr: fr as LocaleMessages,
  de: de as LocaleMessages,
  it: it as LocaleMessages,
};

const getPathValue = (source: unknown, path: string): unknown => {
  return (path?.split?.('.') ?? []).reduce<unknown>((acc, key) => {
    if (!acc || typeof acc !== 'object') {
      return undefined;
    }

    return (acc as Record<string, unknown>)?.[key];
  }, source);
};

export function getMessages(locale: Locale): LocaleMessages {
  return messagesByLocale?.[locale] ?? messagesByLocale[DEFAULT_LOCALE];
}

export function interpolate(template: string, vars: Record<string, string | number> = {}): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) => {
    const value = vars?.[name];
    return value !== undefined && value !== null ? String(value) : '';
  });
}

export function tryParseLocale(raw: string | null | undefined): Locale | null {
  const normalized = raw?.trim?.().toLowerCase?.() ?? '';

  if (!normalized) {
    return null;
  }

  if (normalized === 'zh' || normalized.startsWith('zh-')) return 'zh-CN';
  if (normalized === 'en' || normalized.startsWith('en-')) return 'en';
  if (normalized === 'ja' || normalized.startsWith('ja-')) return 'ja';
  if (normalized === 'ko' || normalized.startsWith('ko-')) return 'ko';
  if (normalized === 'fr' || normalized.startsWith('fr-')) return 'fr';
  if (normalized === 'de' || normalized.startsWith('de-')) return 'de';
  if (normalized === 'it' || normalized.startsWith('it-')) return 'it';

  return null;
}

export function normalizeBrowserLocale(raw: string | null | undefined): Locale {
  return tryParseLocale(raw) ?? DEFAULT_LOCALE;
}

export function parseLocaleFromCookieHeader(cookieHeader: string | undefined | null): Locale | null {
  if (!cookieHeader) {
    return null;
  }

  for (const part of cookieHeader.split(';')) {
    const [keyPart, ...valueParts] = part?.split?.('=') ?? [];
    const key = keyPart?.trim?.() ?? '';

    if (key !== LOCALE_COOKIE_NAME) {
      continue;
    }

    const rawValue = valueParts?.join?.('=')?.trim?.() ?? '';

    try {
      return tryParseLocale(decodeURIComponent(rawValue));
    } catch {
      return tryParseLocale(rawValue);
    }
  }

  return null;
}

export function resolveLocaleForSsr(cookieHeader: string | undefined | null): Locale {
  return parseLocaleFromCookieHeader(cookieHeader) ?? DEFAULT_LOCALE;
}

export function formatLocaleCookieValue(locale: Locale): string {
  return `${LOCALE_COOKIE_NAME}=${encodeURIComponent(locale)}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`;
}

export function setDocumentLocaleCookie(locale: Locale): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = formatLocaleCookieValue(locale);
}

const readStoredLocale = (): Locale | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return tryParseLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
};

const persistLocale = (locale: Locale) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  }

  setDocumentLocaleCookie(locale);
};

type TFunction = (key: string, vars?: Record<string, string | number>) => string;
type RawFunction = <T = unknown>(key: string) => T | null;

type I18nContextValue = {
  locale: Locale;
  t: TFunction;
  getRaw: RawFunction;
  setLocale: (locale: Locale) => void;
  locales: { code: Locale; label: string }[];
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ initialLocale, children }: { initialLocale: Locale; children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const messages = useMemo(() => getMessages(locale), [locale]);
  const fallbackMessages = useMemo(() => getMessages(DEFAULT_LOCALE), []);

  const setLocale = useCallback((nextLocale: Locale) => {
    setLocaleState(nextLocale);
    persistLocale(nextLocale);
  }, []);

  const getRaw = useCallback<RawFunction>(
    <T,>(key: string) => {
      const active = getPathValue(messages, key);
      const fallback = getPathValue(fallbackMessages, key);
      return ((active ?? fallback ?? null) as T | null);
    },
    [fallbackMessages, messages],
  );

  const t = useCallback<TFunction>(
    (key, vars) => {
      const raw = getRaw<string>(key);
      if (typeof raw !== 'string') {
        return key;
      }
      return interpolate(raw, vars);
    },
    [getRaw],
  );

  const locales = useMemo(() => {
    return SUPPORTED_LOCALES.map((code) => ({ code, label: localeLabelMap[code] }));
  }, []);

  const value = useMemo(
    () => ({ locale, t, getRaw, setLocale, locales }),
    [getRaw, locale, locales, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }

  return context;
}

export function LocaleBootstrap() {
  const { locale, setLocale } = useI18n();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storedLocale = readStoredLocale();
    const cookieLocale = parseLocaleFromCookieHeader(document.cookie);
    const browserLocale = normalizeBrowserLocale(window.navigator?.language);
    const nextLocale = storedLocale ?? cookieLocale ?? browserLocale ?? DEFAULT_LOCALE;

    persistLocale(nextLocale);

    if (nextLocale !== locale) {
      setLocale(nextLocale);
    }
  }, [locale, setLocale]);

  return null;
}
